import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Paperclip, Image as ImageIcon, Mic, Search, Edit2, Trash2, Users, User } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InternalChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState({ individual: [], group: null });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' or 'group'
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [subUsers, setSubUsers] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [selectedUserForChat, setSelectedUserForChat] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem('monitoringAdminToken');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setIsAdmin(payload.user_type === 'admin');
    }
    
    // Fetch unread count every 5 seconds
    const interval = setInterval(fetchUnreadCount, 5000);
    fetchUnreadCount();
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      fetchCustomGroups();
      if (isAdmin) {
        fetchSubUsers();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000); // Poll for new messages
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchCustomGroups = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/groups/custom`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomGroups(data);
      }
    } catch (error) {
      console.error('Error fetching custom groups:', error);
    }
  };

  const fetchSubUsers = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/sub-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubUsers(data);
      }
    } catch (error) {
      console.error('Error fetching sub-users:', error);
    }
  };

  const fetchMessages = async () => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/messages/${selectedConversation.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        
        // Mark as read
        await fetch(`${API_URL}/api/internal-chat/mark-read/${selectedConversation.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() && !editingMessageId) return;

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      
      if (editingMessageId) {
        // Edit message
        const response = await fetch(`${API_URL}/api/internal-chat/message/${editingMessageId}/edit`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ message_text: messageText })
        });
        
        if (response.ok) {
          setEditingMessageId(null);
          setMessageText('');
          fetchMessages();
        }
      } else {
        // Send new message
        const payload = {
          message_text: messageText,
          message_type: 'text',
          is_group: activeTab === 'group'
        };

        if (selectedConversation) {
          payload.conversation_id = selectedConversation.id;
        }

        const response = await fetch(`${API_URL}/api/internal-chat/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          setMessageText('');
          fetchMessages();
          fetchConversations();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        const token = localStorage.getItem('monitoringAdminToken');
        const response = await fetch(`${API_URL}/api/internal-chat/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            conversation_id: selectedConversation.id,
            message_type: type,
            attachment: {
              filename: file.name,
              file_data: base64Data,
              file_type: file.type,
              file_size: file.size
            },
            is_group: activeTab === 'group'
          })
        });

        if (response.ok) {
          fetchMessages();
          toast.success('File uploaded successfully');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/message/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchMessages();
        toast.success('Message deleted');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const startEdit = (message) => {
    setEditingMessageId(message.id);
    setMessageText(message.message_text);
  };

  const sendBroadcast = async () => {
    if (!broadcastMessage.trim()) return;

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message_text: broadcastMessage,
          message_type: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Message sent to ${data.sent_to} users`);
        setBroadcastMessage('');
        setShowBroadcast(false);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Failed to send broadcast message');
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) {
      toast.error('Please enter group name and select users');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/internal-chat/groups/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: groupName,
          participants: selectedUsers
        })
      });

      if (response.ok) {
        toast.success('Group created successfully');
        setGroupName('');
        setSelectedUsers([]);
        setShowCreateGroup(false);
        fetchCustomGroups();
        fetchConversations();
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const toggleUserSelection = (userEmail) => {
    setSelectedUsers(prev =>
      prev.includes(userEmail)
        ? prev.filter(u => u !== userEmail)
        : [...prev, userEmail]
    );
  };

  const startNewChatWithUser = async (user) => {
    setShowNewChat(false);
    
    // Get or create conversation with this user
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const recipient = user.email || user.username;
      
      const response = await fetch(`${API_URL}/api/internal-chat/conversation/create?recipient=${encodeURIComponent(recipient)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.conversation) {
          // Refresh conversations list
          await fetchConversations();
          
          // Select the conversation
          setSelectedConversation(data.conversation);
          setActiveTab('individual');
          toast.success(`Chat started with ${user.username}`);
        } else {
          toast.error('Failed to create conversation');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to start new chat');
      }
    } catch (error) {
      console.error('Error starting new chat:', error);
      toast.error('Failed to start new chat');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  };

  const currentUserEmail = localStorage.getItem('monitoringAdminToken') 
    ? JSON.parse(atob(localStorage.getItem('monitoringAdminToken').split('.')[1])).sub 
    : '';

  return (
    <>
      {/* Chat Icon Button */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed right-4 top-20 w-96 h-[600px] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col z-50">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <h3 className="font-bold">Messages</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          {!selectedConversation ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Admin Actions */}
              {isAdmin && (
                <div className="p-3 border-b bg-indigo-50 space-y-2">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowNewChat(true)}
                      size="sm"
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      💬 New Chat with User
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowBroadcast(true)}
                      size="sm"
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      📢 Send to All
                    </Button>
                    <Button
                      onClick={() => setShowCreateGroup(true)}
                      size="sm"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    >
                      ➕ Create Group
                    </Button>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab('individual')}
                  className={`flex-1 px-4 py-3 text-sm font-medium ${
                    activeTab === 'individual'
                      ? 'text-indigo-600 border-b-2 border-indigo-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <User className="h-4 w-4 inline mr-1" />
                  Individual ({conversations.individual.length})
                </button>
                <button
                  onClick={() => setActiveTab('group')}
                  className={`flex-1 px-4 py-3 text-sm font-medium ${
                    activeTab === 'group'
                      ? 'text-indigo-600 border-b-2 border-indigo-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="h-4 w-4 inline mr-1" />
                  Team Chat
                </button>
                {customGroups.length > 0 && (
                  <button
                    onClick={() => setActiveTab('custom')}
                    className={`flex-1 px-4 py-3 text-sm font-medium ${
                      activeTab === 'custom'
                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Custom Groups ({customGroups.length})
                  </button>
                )}
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'individual' ? (
                  conversations.individual.length > 0 ? (
                    conversations.individual.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className="p-4 border-b hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">
                              {conv.participants.find(p => p !== currentUserEmail) || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{conv.last_message || 'No messages yet'}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p className="mb-4">No conversations yet</p>
                      {!isAdmin && (
                        <Button
                          onClick={() => setShowNewChat(true)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Start New Chat
                        </Button>
                      )}
                    </div>
                  )
                ) : activeTab === 'group' ? (
                  <div
                    onClick={async () => {
                      // Get or create team chat
                      const token = localStorage.getItem('monitoringAdminToken');
                      const response = await fetch(`${API_URL}/api/internal-chat/send`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          is_group: true,
                          message_text: '👋',
                          message_type: 'text'
                        })
                      });
                      if (response.ok) {
                        fetchConversations();
                        setTimeout(() => {
                          if (conversations.group) {
                            setSelectedConversation(conversations.group);
                          }
                        }, 500);
                      }
                    }}
                    className="p-4 border-b hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Team Chat - Click to Start
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {conversations.group ? conversations.group.last_message || 'No messages yet' : 'Start team conversation'}
                        </p>
                      </div>
                      {conversations.group && conversations.group.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {conversations.group.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  customGroups.length > 0 ? (
                    customGroups.map((group) => (
                      <div
                        key={group.id}
                        onClick={() => setSelectedConversation(group)}
                        className="p-4 border-b hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{group.name}</p>
                            <p className="text-xs text-gray-500 truncate">{group.last_message || 'No messages yet'}</p>
                          </div>
                          {group.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                              {group.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No custom groups yet</p>
                      {isAdmin && <p className="text-xs mt-2">Click "Create Group" to start</p>}
                    </div>
                  )
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Chat Header */}
              <div className="p-3 border-b flex items-center justify-between bg-gray-50">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  ← Back
                </button>
                <p className="font-semibold text-sm">
                  {selectedConversation.type === 'group' 
                    ? 'Team Chat' 
                    : selectedConversation.participants.find(p => p !== currentUserEmail) || 'Chat'}
                </p>
                <div></div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`mb-4 flex ${msg.sender === currentUserEmail ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${msg.sender === currentUserEmail ? 'bg-indigo-600 text-white' : 'bg-white'} rounded-lg p-3 shadow`}>
                      {msg.sender !== currentUserEmail && (
                        <p className="text-xs font-semibold mb-1">{msg.sender_name}</p>
                      )}
                      {msg.message_type === 'image' && msg.attachment && (
                        <img src={msg.attachment.file_data} alt="attachment" className="max-w-full rounded mb-2" />
                      )}
                      {msg.message_type === 'file' && msg.attachment && (
                        <div className="flex items-center gap-2 mb-2 p-2 bg-gray-100 rounded">
                          <Paperclip className="h-4 w-4" />
                          <span className="text-xs">{msg.attachment.filename}</span>
                        </div>
                      )}
                      <p className="text-sm">{msg.message_text}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs opacity-70">{formatTime(msg.created_at)}</p>
                        {msg.sender === currentUserEmail && !msg.deleted && (
                          <div className="flex gap-2">
                            <button onClick={() => startEdit(msg)} className="hover:opacity-80">
                              <Edit2 className="h-3 w-3" />
                            </button>
                            <button onClick={() => deleteMessage(msg.id)} className="hover:opacity-80">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      {msg.edited && <p className="text-xs opacity-70 mt-1">(edited)</p>}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t bg-white">
                {editingMessageId && (
                  <div className="mb-2 text-xs text-indigo-600">
                    Editing message... <button onClick={() => { setEditingMessageId(null); setMessageText(''); }} className="underline">Cancel</button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'file')}
                  />
                  <input
                    type="file"
                    ref={imageInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'image')}
                  />
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => imageInputRef.current.click()}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <ImageIcon className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    onClick={sendMessage}
                    className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-t-lg">
              <h3 className="text-lg font-bold">📢 Send to All Users</h3>
              <p className="text-sm text-white/90">Message will be sent to all sub-users individually</p>
            </div>
            <div className="p-4">
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Type your message here..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-orange-500"
                rows={5}
              />
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={sendBroadcast}
                  disabled={!broadcastMessage.trim()}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Send to All
                </Button>
                <Button
                  onClick={() => { setShowBroadcast(false); setBroadcastMessage(''); }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4 rounded-t-lg">
              <h3 className="text-lg font-bold">➕ Create Custom Group</h3>
              <p className="text-sm text-white/90">Select users to add to the group</p>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500 mb-4"
              />
              <p className="text-sm font-semibold mb-2">Select Users:</p>
              <div className="space-y-2">
                {subUsers.map((user) => (
                  <label
                    key={user.username || user.email}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.email || user.username)}
                      onChange={() => toggleUserSelection(user.email || user.username)}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="text-sm font-medium">{user.username}</p>
                      <p className="text-xs text-gray-500">{user.role}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Button
                  onClick={createGroup}
                  disabled={!groupName.trim() || selectedUsers.length === 0}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  Create Group ({selectedUsers.length} users)
                </Button>
                <Button
                  onClick={() => { setShowCreateGroup(false); setGroupName(''); setSelectedUsers([]); }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Chat with User Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-4 rounded-t-lg">
              <h3 className="text-lg font-bold">💬 Start New Chat</h3>
              <p className="text-sm text-white/90">
                {isAdmin ? 'Select a sub-user to chat with' : 'Start a conversation with admin'}
              </p>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                {isAdmin ? (
                  subUsers.length > 0 ? (
                    subUsers.map((user) => (
                      <div
                        key={user.username || user.email}
                        onClick={() => startNewChatWithUser(user)}
                        className="flex items-center gap-3 p-3 hover:bg-indigo-50 rounded-lg cursor-pointer border border-gray-200 hover:border-indigo-300 transition-all"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500">{user.role}</p>
                          {user.email && <p className="text-xs text-gray-400">{user.email}</p>}
                        </div>
                        <div className="text-indigo-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No sub-users available</p>
                    </div>
                  )
                ) : (
                  <div
                    onClick={() => startNewChatWithUser({ email: 'monitor@netixfibernet.com', username: 'Admin' })}
                    className="flex items-center gap-3 p-3 hover:bg-indigo-50 rounded-lg cursor-pointer border border-gray-200 hover:border-indigo-300 transition-all"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold">
                      A
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">Admin</p>
                      <p className="text-xs text-gray-500">Administrator</p>
                      <p className="text-xs text-gray-400">monitor@netixfibernet.com</p>
                    </div>
                    <div className="text-indigo-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t">
              <Button
                onClick={() => setShowNewChat(false)}
                variant="outline"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InternalChat;
